package twist.mena.sports.admin.api

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json
import twist.mena.sports.admin.state.AuthState
import twist.mena.sports.admin.state.NavState
import twist.mena.sports.admin.state.Screen
import twist.mena.sports.shared.dto.admin.OffsetPage
import twist.mena.sports.shared.dto.ads.AdResponse
import twist.mena.sports.shared.dto.ads.CreateAdRequest
import twist.mena.sports.shared.dto.ads.UpdateAdRequest

class AdminAdsApi(private val baseUrl: String) {

    private val client = HttpClient {
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                isLenient = true
            })
        }
    }

    private fun authHeaders(): HeadersBuilder.() -> Unit = {
        val token = AuthState.getToken()
        if (token != null) {
            append(HttpHeaders.Authorization, "Bearer $token")
        }
    }

    private suspend fun handleUnauthorized(response: HttpResponse) {
        if (response.status == HttpStatusCode.Unauthorized) {
            AuthState.logout()
            NavState.navigate(Screen.LOGIN)
        }
    }

    suspend fun listAds(
        search: String? = null,
        offset: Int = 0,
        limit: Int = 50
    ): OffsetPage<AdResponse> {
        val response = client.get("$baseUrl/api/v1/admin/ads") {
            headers(authHeaders())
            parameter("offset", offset)
            parameter("limit", limit)
            if (!search.isNullOrBlank()) parameter("search", search)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getLocations(): List<String> {
        val response = client.get("$baseUrl/api/v1/admin/ads/locations") {
            headers(authHeaders())
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun createAd(request: CreateAdRequest): AdResponse {
        val response = client.post("$baseUrl/api/v1/admin/ads") {
            headers(authHeaders())
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleUnauthorized(response)
        if (response.status != HttpStatusCode.Created) {
            throw Exception("Create failed: ${response.status}")
        }
        return response.body()
    }

    suspend fun updateAd(id: String, request: UpdateAdRequest): AdResponse {
        val response = client.patch("$baseUrl/api/v1/admin/ads/$id") {
            headers(authHeaders())
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleUnauthorized(response)
        if (response.status != HttpStatusCode.OK) {
            throw Exception("Update failed: ${response.status}")
        }
        return response.body()
    }

    suspend fun hideAd(id: String): AdResponse {
        val response = client.patch("$baseUrl/api/v1/admin/ads/$id/hide") {
            headers(authHeaders())
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun unhideAd(id: String): AdResponse {
        val response = client.patch("$baseUrl/api/v1/admin/ads/$id/unhide") {
            headers(authHeaders())
        }
        handleUnauthorized(response)
        return response.body()
    }
}
